class LanguageSwapper extends HTMLElement {
    static get observedAttributes() {
        return ['from-label', 'to-label', 'mode', 'disabled-languages'];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.isInit = false;
        this.elements = {};
        this.mode = 'regular';
        this.disabledLanguages = new Set();
        this.languagesMap = new Map();
        
        this._createTemplate();
    }

    _createTemplate() {
        const template = document.createElement('template');
        template.innerHTML = `
            <style>
                .language-swapper {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    max-width: 44rem;
                    margin-top: -5px;
                }

                .select-wrapper:not(:has(select.internal-select.changed)) {
                    height: 51.5px;
                    align-items: center;
                    display: inline-flex;
                    text-align: center;
                    font-size: 1.4rem;
                    color: rgb(0, 0, 0);
                }

                .svg-wrapper {
                    height: .6rem;
                    width: 1rem;
                    pointer-events: none;
                    position: absolute;
                    top: calc(50% - .2rem);
                    right: 1.5rem;
                    display: inline-flex;
                    justify-content: center;
                    align-items: center;
                }

                .select-wrapper:not(:has(select.internal-select.changed))
                .svg-wrapper {
                    display: none;
                }

                .select-group {
                    flex: 1;
                    min-width: 0;
                    position: relative;
                }

                .select-group label {
                    font-size: 1.6rem;
                    color: rgb(0, 0, 0);
                    margin-bottom: .6rem;
                }

                .select-wrapper:after {
                    pointer-events: none;
                    content: "";
                    position: absolute;
                    top: var(--inputs-border-width);
                    right: var(--inputs-border-width);
                    bottom: var(--inputs-border-width);
                    left: var(--inputs-border-width);
                    border: .1rem solid transparent;
                    border-radius: var(--inputs-radius);
                    box-shadow: 0 0 0 var(--inputs-border-width) rgba(var(--color-foreground), var(--inputs-border-opacity));
                    transition: box-shadow var(--duration-short) ease;
                    z-index: 1;
                }

                .select-wrapper:before {
                    pointer-events: none;
                    content: "";
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    /* border-radius: var(--inputs-radius-outset); */
                    box-shadow: var(--inputs-shadow-horizontal-offset) var(--inputs-shadow-vertical-offset) var(--inputs-shadow-blur-radius) rgba(var(--color-shadow), var(--inputs-shadow-opacity));
                    z-index: -1;
                }

                .select-wrapper:has(select.internal-select.changed):hover.select-wrapper:after {
                    box-shadow: 0 0 0 calc(.1rem + var(--inputs-border-width)) rgba(var(--color-foreground), var(--inputs-border-opacity));
                    outline: 0;
                    border-radius: var(--inputs-radius);
                }

                .select-wrapper {
                    position: relative;
                    display: inline-block;
                    width: 100%;
                }

                select.internal-select {
                    width: 100%;
                    padding: 10px 12px;
                    border: 2px solid #e1e5e9;
                    border-radius: 6px;
                    background: white;
                    font-size: 14px;
                    cursor: pointer;
                    appearance: none;
                    background-image: url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23666' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 10px;
                }

                .swap-button {
                    box-shadow: 0 0 0 var(--inputs-border-width) rgba(var(--color-foreground), var(--inputs-border-opacity));
                    border: none;
                    border-radius: 50%;
                    width: 48px;
                    height: 48px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all .2s ease;
                    margin-top: 24px;
                    flex-shrink: 0;
                    background: transparent;
                }

                .swap-button:hover:not(:disabled) {
                    transform: scale(1.05);
                }

                .swap-icon {
                    width: 20px;
                    height: 20px;
                    color: grey;
                    transition: transform .3s ease;
                }

                .swap-button:hover:not(:disabled) .swap-icon {
                    transform: rotate(180deg);
                }

                ::slotted(*) {
                    width: 100%;
                    box-sizing: border-box;
                }

                @media (max-width: 480px) {
                    .language-swapper {
                        flex-direction: column;
                        gap: 10px
                    }

                    .swap-button {
                        margin-top: 0;
                        transform: rotate(90deg);
                    }

                    .swap-button:hover:not(:disabled) {
                        transform: rotate(90deg) scale(1.05);
                    }
                }
            </style>


            <div class="language-swapper">
                <div class="select-group">
                    <label class="form__label fw-600" id="fromLabel">From Language</label>
                    <div class="select-wrapper">
                        <slot name="left"></slot>
                        <span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path></svg>
                        </span>
                        <select class="internal-select" data-side="left" id="fromLanguage"></select>
                    </div>
                </div>

                <button class="swap-button" id="swapButton" aria-label="Swap languages" title="Swap">
                    <svg class="swap-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path stroke-dasharray="12" stroke-dashoffset="12" d="M21 7h-10.5M3 17h10.5"><animate fill="freeze" attributeName="stroke-dashoffset" dur="0.3s" values="12;0"/></path><path stroke-dasharray="8" stroke-dashoffset="8" d="M10 7l4 4M10 7l4 -4M14 17l-4 4M14 17l-4 -4"><animate fill="freeze" attributeName="stroke-dashoffset" begin="0.3s" dur="0.2s" values="8;0"/></path></g></svg>
                </button>

                <div class="select-group">
                    <label class="form__label fw-600" id="toLabel">To Language</label>
                    <div class="select-wrapper">
                        <slot name="right"></slot>
                        <span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path></svg>
                        </span>
                        <select class="internal-select" data-side="right" id="toLanguage"></select>
                    </div>
                </div>
            </div>
        `;
        
        this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        if (!this.isInit) {
            this._init();
        }

        this._bindSlotChanges();
        this._bindEvents();
        this._applyAttributes();
    }

    attributeChangedCallback(name, oldV, newV) {
        if (!this.isInit || oldV === newV) {
            return;
        }

        if (name === 'from-label') this._setLabel('fromLabel', newV);
        if (name === 'to-label') this._setLabel('toLabel', newV);
        if (name === 'mode') this.setMode(newV);
        if (name === 'disabled-languages') this.setDisabledLanguages(newV);
    }

    _init() {
        this.elements.fromLabel = this.shadowRoot.getElementById('fromLabel');
        this.elements.toLabel = this.shadowRoot.getElementById('toLabel');
        this.elements.fromSelect = this.shadowRoot.getElementById('fromLanguage');
        this.elements.toSelect = this.shadowRoot.getElementById('toLanguage');
        this.elements.swapButton = this.shadowRoot.getElementById('swapButton');
        this.elements.leftSlot = this.shadowRoot.querySelector('slot[name="left"]');
        this.elements.rightSlot = this.shadowRoot.querySelector('slot[name="right"]');

        this._addDefaultOptions();

        this.isInit = true;
    }

    _addDefaultOptions() {
        const defaultLanguages = [
            { value: 'English', label: 'English', sworn: true },
            { value: 'Arabic (Egypt)', label: 'Arabic (Egypt)', sworn: true },
            { value: 'Italian', label: 'Italian', sworn: true },
            { value: 'French', label: 'French', sworn: true },
            { value: 'German', label: 'German', sworn: true },
        ];

        defaultLanguages.forEach(lang => {
            if (!Array.from(this.elements.fromSelect.options).some(opt => opt.value === lang.value)) {
                const option = document.createElement('option');
                option.value = lang.value;
                option.textContent = lang.label;
                if (lang.sworn) option.setAttribute('data-sworn', '');
                this.elements.fromSelect.appendChild(option);
            }

            if (!Array.from(this.elements.toSelect.options).some(opt => opt.value === lang.value)) {
                const option = document.createElement('option');
                option.value = lang.value;
                option.textContent = lang.label;
                if (lang.sworn) option.setAttribute('data-sworn', '');
                this.elements.toSelect.appendChild(option);
            }
        });
    }

    _bindSlotChanges() {
        this.elements.leftSlot.addEventListener('slotchange', () => this._onSlotChange('left'));
        this.elements.rightSlot.addEventListener('slotchange', () => this._onSlotChange('right'));

        this._onSlotChange('left');
        this._onSlotChange('right');
    }


    _bindEvents() {
        this.elements.swapButton.addEventListener('click', () => this.handleSwap());
        this.elements.fromSelect.addEventListener('change', () => this._onInternalChange('left'));
        this.elements.toSelect.addEventListener('change', () => this._onInternalChange('right'));
    }

    _applyAttributes() {
        this._setLabel('fromLabel', this.getAttribute('from-label'));
        this._setLabel('toLabel', this.getAttribute('to-label'));

        const mode = this.getAttribute('mode');
        if (mode) {
            this.setMode(mode);
        }

        const disabled = this.getAttribute('disabled-languages');
        if (disabled) {
            this.setDisabledLanguages(disabled);
        }
    }

    _setLabel(key, value) {
        if (this.elements[key] && value != null) {
            this.elements[key].textContent = value;
        }
    }

    _onSlotChange(side) {
        const slot = side === 'left' ? this.elements.leftSlot : this.elements.rightSlot;
        const assigned = slot.assignedElements ? slot.assignedElements({flatten: true}) : [];
        const has = assigned && assigned.length > 0;
        const internal = side === 'left' ? this.elements.fromSelect : this.elements.toSelect;

        internal.style.display = has ? 'none' : '';

        if (has) {
            const el = assigned[0];
            if (el.tagName && el.tagName.toLowerCase() === 'select') {
                this._buildLanguagesFromDOM();
                el.addEventListener('change', () => this._onSlottedSelectChange(side, el));
                internal.classList.add('changed');
            } else {
                this._buildLanguagesFromDOM();
                internal.classList.remove('changed');
            }
        } else {
            this._buildLanguagesFromDOM();
        }

        this.applyModeRestrictions();
    }

_buildLanguagesFromDOM() {
    this.languagesMap.clear();

    const leftAssigned = this.elements.leftSlot.assignedElements ? this.elements.leftSlot.assignedElements({flatten: true})[0] : null;
    const rightAssigned = this.elements.rightSlot.assignedElements ? this.elements.rightSlot.assignedElements({flatten: true})[0] : null;

    const ingestSelectOptions = (selectEl) => {
        if (!selectEl || !selectEl.options) return;
        Array.from(selectEl.options).forEach(opt => {
            const val = opt.value;
            if (!val) return;
            const label = String(opt.textContent || opt.innerText || val).trim();
            const sworn = opt.hasAttribute('data-sworn') || opt.dataset.sworn === 'true' || opt.dataset.sworn === '';
            if (!this.languagesMap.has(val)) {
                this.languagesMap.set(val, { label, sworn });
            } else {
                const entry = this.languagesMap.get(val);
                if (sworn && !entry.sworn) entry.sworn = true;
            }
        });
    };

    if (leftAssigned && leftAssigned.tagName && leftAssigned.tagName.toLowerCase() === 'select') {
        ingestSelectOptions(leftAssigned);
    }

    if (rightAssigned && rightAssigned.tagName && rightAssigned.tagName.toLowerCase() === 'select') {
        ingestSelectOptions(rightAssigned);
    }

    ingestSelectOptions(this.elements.fromSelect);
    ingestSelectOptions(this.elements.toSelect);
}

    _onSlottedSelectChange(side, el) {
        this.dispatchEvent(new CustomEvent('language-changed', {
            detail: {
                source: side,
                from: this.fromLanguage,
                to: this.toLanguage,
                mode: this.mode
            }, bubbles: true, composed: true
        }));
    }

    _onInternalChange(side) {
        this.dispatchEvent(new CustomEvent('language-changed', {
            detail: {
                source: side,
                from: this.fromLanguage,
                to: this.toLanguage,
                mode: this.mode
            }, bubbles: true, composed: true
        }));
    }


    setMode(mode) {
        const ok = ['regular', 'sworn'];
        if (!ok.includes(mode)) {
            console.warn('invalid mode', mode);
            mode = 'regular';
        }

        this.mode = mode;
        this.applyModeRestrictions();
        this.dispatchEvent(new CustomEvent('mode-change', {detail: {mode}, bubbles: true, composed: true}));
    }

    setDisabledLanguages(list) {
        if (typeof list === 'string') {
            try {
                list = JSON.parse(list);
            } catch {
                list = list.split(',').map(s => s.trim());
            }
        }

        this.disabledLanguages = new Set(Array.isArray(list) ? list : []);
        this.applyModeRestrictions();
    }

applyModeRestrictions() {
    for (let option of this.elements.fromSelect.options) {
        option.disabled = false;
        if (this.mode === 'sworn' && !(option.hasAttribute('data-sworn') || option.dataset.sworn === 'true' || option.dataset.sworn === '')) {
            option.disabled = true;
        }
        if (this.disabledLanguages.has(option.value)) {
            option.disabled = true;
        }
    }

    for (let o of this.elements.toSelect.options) {
        o.disabled = false;
        if (this.mode === 'sworn' && !(o.hasAttribute('data-sworn') || o.dataset.sworn === 'true' || o.dataset.sworn === '')) {
            o.disabled = true;
        }
        if (this.disabledLanguages.has(o.value)) {
            o.disabled = true;
        }
    }

    const leftAssigned = this.elements.leftSlot.assignedElements ? this.elements.leftSlot.assignedElements({flatten: true})[0] : null;
    const rightAssigned = this.elements.rightSlot.assignedElements ? this.elements.rightSlot.assignedElements({flatten: true})[0] : null;

    if (leftAssigned && leftAssigned.tagName && leftAssigned.tagName.toLowerCase() === 'select') {
        for (let o of leftAssigned.options) {
            o.disabled = false;
            if (this.mode === 'sworn' && !(o.hasAttribute('data-sworn') || o.dataset.sworn === 'true' || o.dataset.sworn === '')) o.disabled = true;
            if (this.disabledLanguages.has(o.value)) o.disabled = true;
        }
    }

    if (rightAssigned && rightAssigned.tagName && rightAssigned.tagName.toLowerCase() === 'select') {
        for (let o of rightAssigned.options) {
            o.disabled = false;
            if (this.mode === 'sworn' && !(o.hasAttribute('data-sworn') || o.dataset.sworn === 'true' || o.dataset.sworn === '')) o.disabled = true;
            if (this.disabledLanguages.has(o.value)) o.disabled = true;
        }
    }

    this._ensureSelectionValidity();
}

_ensureSelectionValidity() {
    if (this.elements.fromSelect.value && this.elements.fromSelect.querySelector(`option[value="${this.elements.fromSelect.value}"]:disabled`)) {
        this.elements.fromSelect.value = '';
    }

    if (this.elements.toSelect.value && this.elements.toSelect.querySelector(`option[value="${this.elements.toSelect.value}"]:disabled`)) {
        this.elements.toSelect.value = '';
    }

    const leftAssigned = this.elements.leftSlot.assignedElements ? this.elements.leftSlot.assignedElements({flatten: true})[0] : null;
    const rightAssigned = this.elements.rightSlot.assignedElements ? this.elements.rightSlot.assignedElements({flatten: true})[0] : null;

    if (leftAssigned && leftAssigned.tagName && leftAssigned.tagName.toLowerCase() === 'select') {
        const val = leftAssigned.value;
        if (val && leftAssigned.querySelector(`option[value="${val}"]:disabled`)) {
            leftAssigned.value = '';
            leftAssigned.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    if (rightAssigned && rightAssigned.tagName && rightAssigned.tagName.toLowerCase() === 'select') {
        const val = rightAssigned.value;
        if (val && rightAssigned.querySelector(`option[value="${val}"]:disabled`)) {
            rightAssigned.value = '';
            rightAssigned.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
}

    _isSworn(value) {
        if (!value) {
            return false;
        }
        const entry = this.languagesMap.get(value);
        if (entry) {
            return !!entry.sworn;
        }
        return false;
    }

    _valueOfSlotted(el) {
        if (!el) return '';
        if (el.dataset && el.dataset.value) return el.dataset.value;
        const span = el.querySelector && el.querySelector('[data-value]');
        if (span) return span.dataset.value;
        if (el.tagName && el.tagName.toLowerCase() === 'select') return String(el.value || '');
        return String(el.textContent || '').trim();
    }

    handleSwap() {
        const leftAssigned = this.elements.leftSlot.assignedElements ? this.elements.leftSlot.assignedElements({flatten: true})[0] : null;
        const rightAssigned = this.elements.rightSlot.assignedElements ? this.elements.rightSlot.assignedElements({flatten: true})[0] : null;

        const leftSel = this.elements.fromSelect;
        const rightSel = this.elements.toSelect;
        const leftVal = leftSel.value;
        const rightVal = rightSel.value;

        if (leftAssigned && rightAssigned) {
            try {
                leftAssigned.setAttribute('slot', '__tmp__swap__');
                rightAssigned.setAttribute('slot', 'left');
                leftAssigned.setAttribute('slot', 'right');

                const tmp = this.querySelector('[slot="__tmp__swap__"]');
                if (tmp) tmp.setAttribute('slot', 'left');
            } catch (e) {
                console.error(e);
            }

            this._postSwapEmit(leftAssigned, rightAssigned);


            setTimeout(() => {
                this._onSlotChange('left');
                this._onSlotChange('right');
            }, 0);

            return;
        }

        if (leftAssigned && !rightAssigned) {
            leftAssigned.setAttribute('slot', 'right');
            leftSel.value = rightVal || '';
            rightSel.value = leftVal || '';

            this._postSwapEmit(leftAssigned, null, leftVal, rightVal);

            setTimeout(() => {
                this._onSlotChange('left');
                this._onSlotChange('right');
            }, 0);

            return;
        }

        if (rightAssigned && !leftAssigned) {
            rightAssigned.setAttribute('slot', 'left');
            leftSel.value = rightVal || '';
            rightSel.value = leftVal || '';

            this._postSwapEmit(null, rightAssigned, leftVal, rightVal);

            setTimeout(() => {
                this._onSlotChange('left');
                this._onSlotChange('right');
            }, 0);

            return;
        }

        if (!leftAssigned && !rightAssigned) {
            if (!leftVal || !rightVal || leftVal === rightVal) return;

            leftSel.value = rightVal;
            rightSel.value = leftVal;

            this._postSwapEmit(null, null, leftVal, rightVal);
        }
    }

    _postSwapEmit(oldLeftNode, oldRightNode, oldLeftValue, oldRightValue) {
        const detail = {mode: this.mode};

        if (oldLeftNode || oldRightNode) {
            detail.oldFrom = oldLeftNode ? this._valueOfSlotted(oldLeftNode) : (oldLeftValue || this.elements.fromSelect.value);
            detail.oldTo = oldRightNode ? this._valueOfSlotted(oldRightNode) : (oldRightValue || this.elements.toSelect.value);
        } else {
            detail.oldFrom = oldLeftValue || null;
            detail.oldTo = oldRightValue || null;
        }

        detail.newFrom = this.fromLanguage;
        detail.newTo = this.toLanguage;

        this.dispatchEvent(new CustomEvent('languages-swapped', {detail, bubbles: true, composed: true}));
    }

    fromLanguageSelect() {
        return this.elements.fromSelect;
    }

    get fromLanguage() {
        const leftAssigned = this.elements.leftSlot.assignedElements ? this.elements.leftSlot.assignedElements({flatten: true})[0] : null;
        if (leftAssigned) return this._valueOfSlotted(leftAssigned);
        return this.elements.fromSelect.value;
    }

    set fromLanguage(val) {
        const leftAssigned = this.elements.leftSlot.assignedElements ? this.elements.leftSlot.assignedElements({flatten: true})[0] : null;
        if (leftAssigned && leftAssigned.tagName && leftAssigned.tagName.toLowerCase() === 'select') {
            leftAssigned.value = val;
            leftAssigned.dispatchEvent(new Event('change', {bubbles: true}));
        } else {
            this.elements.fromSelect.value = val;
            this._onInternalChange('left');
        }
    }

    toLanguageSelect() {
        return this.elements.toSelect;
    }

    get toLanguage() {
        const rightAssigned = this.elements.rightSlot.assignedElements ? this.elements.rightSlot.assignedElements({flatten: true})[0] : null;
        if (rightAssigned) return this._valueOfSlotted(rightAssigned);
        return this.elements.toSelect.value;
    }

    set toLanguage(val) {
        const rightAssigned = this.elements.rightSlot.assignedElements ? this.elements.rightSlot.assignedElements({flatten: true})[0] : null;

        if (rightAssigned && rightAssigned.tagName && rightAssigned.tagName.toLowerCase() === 'select') {
            rightAssigned.value = val;
            rightAssigned.dispatchEvent(new Event('change', {bubbles: true}));
        } else {
            this.elements.toSelect.value = val;
            this._onInternalChange('right');
        }
    }

    fromSelectedOption() {
        const leftAssigned = this.elements.leftSlot?.assignedElements
            ? this.elements.leftSlot.assignedElements({ flatten: true })[0]
            : null;

        if (leftAssigned && leftAssigned.tagName && leftAssigned.tagName.toLowerCase() === 'select') {
            return leftAssigned.options[leftAssigned.selectedIndex] || null;
        }

        const slottedVal = this._valueOfSlotted(leftAssigned);
        if (slottedVal) {
            const match = this.elements.fromSelect.querySelector(`option[value="${(CSS && CSS.escape) ? CSS.escape(slottedVal) : slottedVal}"]`);
            if (match) return match;
        }

        const internal = this.elements.fromSelect;
        return internal?.options?.[internal.selectedIndex] || null;
    }

    toSelectedOption() {
        const rightAssigned = this.elements.rightSlot?.assignedElements
            ? this.elements.rightSlot.assignedElements({ flatten: true })[0]
            : null;

        if (rightAssigned && rightAssigned.tagName && rightAssigned.tagName.toLowerCase() === 'select') {
            return rightAssigned.options[rightAssigned.selectedIndex] || null;
        }

        const slottedVal = this._valueOfSlotted(rightAssigned);
        if (slottedVal) {
            const match = this.elements.toSelect.querySelector(`option[value="${(CSS && CSS.escape) ? CSS.escape(slottedVal) : slottedVal}"]`);
            if (match) return match;
        }

        const internal = this.elements.toSelect;
        return internal?.options?.[internal.selectedIndex] || null;
    }

}

if (!customElements.get('language-swapper')) {
    customElements.define('language-swapper', LanguageSwapper);
}
